import { Signal, computed } from '@preact/signals';
import type { ProfileDTO } from '@/services/generated/ECAPISubscriptionsDTOs';
import * as ECAPI from './EcommerceService.ts';
import {
  type AboutSubscriptions,
  type Subscription,
  type SubscriptionsByDuration,
  findDurationFromSubscriptionId
} from '@/domain/Subscription';
import {
  aboutSubscriptionsFromEcapiDto,
  fromEcapiDto
} from '@/domain/assemblers/SubscriptionAssembler.ts';

import { isLoading, cart } from './CartService.ts';

export async function getSubscriptions(
  market: string,
  channel: string,
  locale: string,
  cacheBurst?: boolean
): Promise<Subscription[]> {
  const [subscriptions, subscriptionsContent] = await Promise.all([
    ECAPI.subscriptions(market, locale, cacheBurst),
    ECAPI.cms(market, channel, 'catalog_subscriptions', locale, cacheBurst)
  ]);

  const subscriptionMap = new Map<string, ProfileDTO>(
    subscriptions.profiles.map((sub) => [sub.id, sub])
  );

  const subs = Object.keys(subscriptionsContent?.payload?.subscriptions ?? {})
    .map((subscriptionId: string) => {
      const dto = subscriptionMap.get(subscriptionId);

      if (!dto) return null;

      // @ts-ignore
      const content = subscriptionsContent?.payload?.subscriptions[
        subscriptionId
      ] as any;

      return fromEcapiDto(dto, content);
    })
    .filter((sub) => sub !== null);

  // @ts-ignore
  return subs;
}

export async function getAboutSubscriptions(
  market: string,
  channel: string,
  locale: string,
  cacheBurst?: boolean
): Promise<AboutSubscriptions> {
  const subscriptionsContent = await ECAPI.cms(
    market,
    channel,
    'catalog_subscriptions',
    locale,
    cacheBurst
  );

  const aboutSubscriptions: any =
    subscriptionsContent?.payload?.aboutSubscriptions;

  return aboutSubscriptionsFromEcapiDto(aboutSubscriptions);
}

export function initialiseSubscriptionFlow(
  subscriptionsByDuration: SubscriptionsByDuration,
  $cart = cart
) {
  const explicitDuration = new Signal<number | false>(false);
  const explicitSubscriptionId = new Signal<string>('');
  const subscriptionByDurationEntries = Object.entries(subscriptionsByDuration);

  const selectedDuration = computed(() => {
    if (explicitDuration.value) {
      return explicitDuration.value;
    }
    if ($cart.value?.subscription?.profileId && !isLoading.value) {
      return findDurationFromSubscriptionId(
        subscriptionsByDuration,
        $cart.value?.subscription?.profileId
      );
    }
    const duration = parseInt(subscriptionByDurationEntries?.[0]?.[0], 10);
    return Number.isInteger(duration) ? duration : false;
  });

  const selectedSubscriptionId = computed(() => {
    if (explicitSubscriptionId.value) {
      return explicitSubscriptionId.value;
    }
    if ($cart.value?.subscription?.profileId && !isLoading.value) {
      return $cart.value?.subscription?.profileId;
    }
    return subscriptionByDurationEntries?.[0]?.[1]?.[0]?.id;
  });

  function setSubscriptionId(newSubscriptionId: string) {
    explicitSubscriptionId.value = newSubscriptionId;
  }

  function setDuration(newDuration: number) {
    explicitDuration.value = newDuration;
    explicitSubscriptionId.value = subscriptionsByDuration[newDuration][0].id;
  }

  function reset() {
    explicitDuration.value = false;
    explicitSubscriptionId.value = '';
  }

  return {
    selectedDuration,
    selectedSubscriptionId,
    setSubscriptionId,
    setDuration,
    reset
  };
}
